import type { Result } from '@bizzdesign/sdk-knowledgegraph';

/**
 * Collects every item from an SDK {@link Result} by walking its pages via
 * `asPages()`. Use this for endpoints whose result supports pagination — e.g.
 * `getObjects` or `getRelations` queries.
 *
 * @typeParam T - The element type contained in the result.
 * @param resultOfSDK - The SDK {@link Result} to page through.
 * @param pageSize - The number of items requested per page.
 * @returns A promise that resolves to every item aggregated across all pages,
 *   in page order.
 * @throws An `ApiError` with message `Pagination is not supported for this
 *   result` when the endpoint's result does not support `asPages()` (e.g.
 *   `getNeighboringRelationsOfObject`); use {@link getAllStreamData} instead.
 */
export const getAllPagesData = async <T>(
  resultOfSDK: Result<T>,
  pageSize: number,
): Promise<T[]> => {
  const pages = resultOfSDK.asPages({ pageSize });
  const pageNumber = await pages.getNumberOfPages();
  const aggregator: T[] = [];
  for (let i = 0; i < pageNumber; i++) {
    const pageData = await pages.getPage(i);
    aggregator.push(...pageData);
  }
  return aggregator;
};

/**
 * Collects every item from an SDK {@link Result} by draining its async
 * `stream()`. Use this for endpoints whose result does not support `asPages()`
 * — e.g. `getNeighboringRelationsOfObject`, where {@link getAllPagesData}
 * throws. Prefer this over the deprecated `Result.getAll()`.
 *
 * @typeParam T - The element type produced by the result's stream.
 * @param resultOfSDK - The SDK {@link Result} to drain.
 * @returns A promise that resolves to every streamed item, in stream order.
 */
export const getAllStreamData = async <T>(
  resultOfSDK: Result<T>,
): Promise<T[]> => {
  const aggregator: T[] = [];
  for await (const item of resultOfSDK.stream()) {
    aggregator.push(item);
  }
  return aggregator;
};
